// Executive overview charts, drawn as SVG in HELM's own colour tokens so they follow the
// light and dark themes. Each has a plain-text title for hover detail and screen readers.
import { Link } from 'react-router-dom';
import { ArrowRight, Gavel, PackageCheck } from 'lucide-react';
import type { HealthLevel } from '@/lib/tracker';
import { cn, fmtDate } from '@/lib/utils';
import { Card, IconChip, type Tone } from '@/components/ui/misc';

const DAY = 86_400_000;
const t = (iso: string) => Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const HEALTH_VAR: Record<HealthLevel, string> = { GREEN: 'var(--ok)', AMBER: 'var(--warn)', RED: 'var(--bad)' };
const signed = (n: number) => (n === 0 ? 'On plan' : n > 0 ? `${n} days late` : `${-n} days early`);

export function ChartCard({
  title,
  action,
  children,
  className,
  icon,
  tone = 'brand',
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  icon?: React.ReactNode;
  tone?: Tone;
}) {
  return (
    <Card className={cn('flex flex-col p-0', className)}>
      <div className="flex items-center gap-3 border-b border-border px-4 py-2.5">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          {icon && <IconChip tone={tone}>{icon}</IconChip>}
          {title}
        </h2>
        {action && <div className="ml-auto text-xs">{action}</div>}
      </div>
      <div className="flex-1 px-4 py-3">{children}</div>
    </Card>
  );
}

// ── Needs you ──

const NEEDS_CARD = 'flex items-start gap-3 rounded-lg border px-4 py-3 transition hover:shadow-sm';
const NEEDS_TONE = {
  none: 'border-border bg-surface hover:border-border-strong',
  warn: 'border-warn/40 bg-warn-soft hover:border-warn/70',
  bad: 'border-bad/40 bg-bad-soft hover:border-bad/70',
};
const NEEDS_ICON = { none: 'text-muted', warn: 'text-warn', bad: 'text-bad' };

export function NeedsYou({
  programId,
  clientShortName,
  decisions,
  awaiting,
}: {
  programId: string;
  clientShortName: string;
  decisions: Array<{ id: string; ref: string; title: string; due: string | null; overdue: boolean }>;
  awaiting: Array<{ id: string; name: string; days: number | null }>;
}) {
  const p = (path: string) => `/p/${programId}/${path}`;
  const firstDecision = decisions[0];
  const longest = [...awaiting].sort((a, b) => (b.days ?? 0) - (a.days ?? 0))[0];
  const overdue = decisions.filter((d) => d.overdue).length;
  // Colour only when something waits: amber while pending, red once overdue (reviews: 14 days).
  const decisionLevel = overdue ? 'bad' : decisions.length ? 'warn' : null;
  const reviewLevel = (longest?.days ?? 0) >= 14 ? 'bad' : awaiting.length ? 'warn' : null;
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <Link to={p('raid?type=DECISION')} className={cn(NEEDS_CARD, NEEDS_TONE[decisionLevel ?? 'none'])}>
        <Gavel className={cn('mt-0.5 size-5', NEEDS_ICON[decisionLevel ?? 'none'])} />
        <div className="min-w-0 flex-1">
          <p className="text-sm">
            <span className="text-2xl font-semibold tabular-nums">{decisions.length}</span>{' '}
            <span className="text-muted">decision{decisions.length === 1 ? '' : 's'} pending</span>
            {overdue > 0 && <span className="ml-2 text-xs font-medium text-bad">{overdue} overdue</span>}
          </p>
          <p className="truncate text-[13px] text-muted">
            {firstDecision
              ? `${firstDecision.ref} ${firstDecision.title}${firstDecision.due ? `, needed by ${fmtDate(firstDecision.due)}` : ''}`
              : 'Nothing waiting on the committee.'}
          </p>
        </div>
        <ArrowRight className="mt-1 size-4 text-subtle" />
      </Link>
      <Link to={p('deliverables')} className={cn(NEEDS_CARD, NEEDS_TONE[reviewLevel ?? 'none'])}>
        <PackageCheck className={cn('mt-0.5 size-5', NEEDS_ICON[reviewLevel ?? 'none'])} />
        <div className="min-w-0 flex-1">
          <p className="text-sm">
            <span className="text-2xl font-semibold tabular-nums">{awaiting.length}</span>{' '}
            <span className="text-muted">
              deliverable{awaiting.length === 1 ? '' : 's'} awaiting {clientShortName} review
            </span>
          </p>
          <p className="truncate text-[13px] text-muted">
            {longest
              ? `${longest.name}${longest.days != null ? `, waiting ${longest.days} day${longest.days === 1 ? '' : 's'}` : ''}`
              : 'Nothing is waiting on review.'}
          </p>
        </div>
        <ArrowRight className="mt-1 size-4 text-subtle" />
      </Link>
    </div>
  );
}

// ── Timeline against the PA plan ──

interface TimelinePhase {
  id: string;
  number: number;
  name: string;
  plannedStart: string;
  plannedEnd: string;
  forecastStart: string;
  forecastEnd: string;
  health: { effective: HealthLevel };
  milestones: Array<{ id: string; name: string; planned: string; forecast: string; achieved: string | null }>;
}

export function Timeline({ phases, today }: { phases: TimelinePhase[]; today: string }) {
  if (!phases.length) return <p className="text-sm text-muted">No phases yet.</p>;
  const dates = phases.flatMap((p) => [p.plannedStart, p.plannedEnd, p.forecastStart, p.forecastEnd]);
  const first = new Date(Math.min(...dates.map(t)));
  const from = Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), 1);
  const lastT = Math.max(...dates.map(t));
  const last = new Date(lastT);
  const to = Date.UTC(last.getUTCFullYear(), last.getUTCMonth() + 1, 1);
  const W = 1000;
  const LABEL = 260;
  const RIGHT = 100;
  const ROW = 38;
  const TOP = 22;
  const H = TOP + phases.length * ROW + 20;
  const x = (ms: number) => LABEL + ((ms - from) / (to - from)) * (W - LABEL - RIGHT);
  const months: number[] = [];
  for (let d = new Date(from); d.getTime() < to; d.setUTCMonth(d.getUTCMonth() + 1)) months.push(d.getTime());
  const step = Math.max(1, Math.ceil(months.length / 12));
  const nowX = x(t(today));
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Phases planned against forecast">
        {months.map((m, i) => (
          <g key={m}>
            <line x1={x(m)} x2={x(m)} y1={TOP - 4} y2={H} stroke="var(--border)" strokeWidth={1} />
            {i % step === 0 && (
              <text x={x(m) + 3} y={12} fontSize={11} fill="var(--muted)">
                {MONTHS[new Date(m).getUTCMonth()]}
                {new Date(m).getUTCMonth() === 0 || i === 0 ? ` ${new Date(m).getUTCFullYear()}` : ''}
              </text>
            )}
          </g>
        ))}
        {phases.map((p, i) => {
          const y = TOP + i * ROW;
          const slip = Math.round((t(p.forecastEnd) - t(p.plannedEnd)) / DAY);
          const colour = HEALTH_VAR[p.health.effective];
          return (
            <g key={p.id}>
              <text x={0} y={y + 20} fontSize={13} fill="var(--text)">
                {`${p.number}. ${p.name.length > 34 ? `${p.name.slice(0, 33)}…` : p.name}`}
                <title>{p.name}</title>
              </text>
              <rect
                x={x(t(p.plannedStart))}
                y={y + 6}
                width={Math.max(2, x(t(p.plannedEnd)) - x(t(p.plannedStart)))}
                height={7}
                rx={2}
                fill="var(--border-strong)"
              >
                <title>{`PA plan: ${fmtDate(p.plannedStart)} to ${fmtDate(p.plannedEnd)}`}</title>
              </rect>
              <rect
                x={x(t(p.forecastStart))}
                y={y + 15}
                width={Math.max(2, x(t(p.forecastEnd)) - x(t(p.forecastStart)))}
                height={11}
                rx={2}
                fill={colour}
              >
                <title>{`Forecast: ${fmtDate(p.forecastStart)} to ${fmtDate(p.forecastEnd)}`}</title>
              </rect>
              {p.milestones.map((m) => {
                const mx = x(t(m.achieved ?? m.forecast));
                return (
                  <path
                    key={m.id}
                    d={`M ${mx} ${y + 14} l 6 6 l -6 6 l -6 -6 z`}
                    fill={m.achieved ? 'var(--text)' : 'var(--surface)'}
                    stroke="var(--text)"
                    strokeWidth={1.4}
                  >
                    <title>
                      {m.achieved
                        ? `${m.name}: achieved ${fmtDate(m.achieved)}`
                        : `${m.name}: forecast ${fmtDate(m.forecast)} (planned ${fmtDate(m.planned)})`}
                    </title>
                  </path>
                );
              })}
              <text
                x={W - RIGHT + 10}
                y={y + 24}
                fontSize={12}
                fill={slip > 0 ? 'var(--bad)' : 'var(--muted)'}
                fontWeight={slip > 0 ? 600 : 400}
              >
                {signed(slip)}
              </text>
            </g>
          );
        })}
        {nowX >= LABEL && nowX <= W - RIGHT && (
          <g>
            <line
              x1={nowX}
              x2={nowX}
              y1={TOP - 4}
              y2={H - 14}
              stroke="var(--brand)"
              strokeWidth={1.5}
              strokeDasharray="4 3"
            />
            <text x={nowX} y={H - 2} fontSize={10} textAnchor="middle" fill="var(--brand-text)" fontWeight={600}>
              Today
            </text>
          </g>
        )}
      </svg>
      <p className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
        <span>
          <span className="mr-1 inline-block h-1.5 w-4 rounded-sm bg-border-strong align-middle" /> PA plan
        </span>
        <span>
          <span className="mr-1 inline-block h-2.5 w-4 rounded-sm bg-ok align-middle" /> Forecast, coloured by phase
          health
        </span>
        <span>◆ Milestone (filled when achieved)</span>
      </p>
    </div>
  );
}

// ── Risk picture ──

const cellTone = (score: number) =>
  score >= 15 ? 'bg-bad-soft text-bad' : score >= 8 ? 'bg-warn-soft text-warn' : 'bg-ok-soft text-ok';

export function RiskPicture({
  programId,
  heat,
  risks,
  issues,
}: {
  programId: string;
  heat: number[][];
  risks: { HIGH: number; MEDIUM: number; LOW: number; UNSCORED: number };
  issues: { CRITICAL: number; HIGH: number; MEDIUM: number; LOW: number };
}) {
  const openRisks = risks.HIGH + risks.MEDIUM + risks.LOW + risks.UNSCORED;
  const openIssues = issues.CRITICAL + issues.HIGH + issues.MEDIUM + issues.LOW;
  return (
    <div className="flex flex-wrap items-start gap-6">
      <div>
        <div className="flex">
          <div className="mr-1 flex flex-col justify-around pb-5 text-[10px] text-muted">
            {[5, 4, 3, 2, 1].map((i) => (
              <span key={i} className="h-8 leading-8">
                {i}
              </span>
            ))}
          </div>
          <div>
            <div className="grid grid-cols-5 gap-1">
              {[5, 4, 3, 2, 1].flatMap((imp) =>
                [1, 2, 3, 4, 5].map((l) => {
                  const n = heat[imp - 1]?.[l - 1] ?? 0;
                  return (
                    <div
                      key={`${imp}${l}`}
                      title={`Impact ${imp}, likelihood ${l}: ${n} open risk${n === 1 ? '' : 's'}`}
                      className={cn(
                        'flex size-8 items-center justify-center rounded text-xs font-semibold',
                        cellTone(imp * l),
                        !n && 'opacity-40',
                      )}
                    >
                      {n || ''}
                    </div>
                  );
                }),
              )}
            </div>
            <div className="mt-1 grid grid-cols-5 gap-1 text-center text-[10px] text-muted">
              {[1, 2, 3, 4, 5].map((l) => (
                <span key={l}>{l}</span>
              ))}
            </div>
          </div>
        </div>
        <p className="mt-1 text-[11px] text-muted">Likelihood across, impact up</p>
      </div>
      <div className="min-w-[160px] flex-1 space-y-3 text-[13px]">
        <div>
          <p className="mb-1 font-medium">
            {openRisks} open risk{openRisks === 1 ? '' : 's'}
          </p>
          <Counts
            rows={[
              ['High', risks.HIGH, 'bg-bad'],
              ['Medium', risks.MEDIUM, 'bg-warn'],
              ['Low', risks.LOW, 'bg-ok'],
            ]}
          />
        </div>
        <div>
          <p className="mb-1 font-medium">
            {openIssues} open issue{openIssues === 1 ? '' : 's'}
          </p>
          <Counts
            rows={[
              ['Critical', issues.CRITICAL, 'bg-bad'],
              ['High', issues.HIGH, 'bg-warn'],
              ['Medium', issues.MEDIUM + issues.LOW, 'bg-subtle'],
            ]}
          />
        </div>
        <Link
          to={`/p/${programId}/raid`}
          className="inline-flex items-center gap-1 text-xs text-brand-text hover:underline"
        >
          RAID log <ArrowRight className="size-3" />
        </Link>
      </div>
    </div>
  );
}

function Counts({ rows }: { rows: Array<[string, number, string]> }) {
  return (
    <ul className="space-y-0.5">
      {rows.map(([label, n, dot]) => (
        <li key={label} className="flex items-center gap-2 text-muted">
          <span className={cn('size-2 rounded-full', dot)} />
          <span className="flex-1">{label}</span>
          <span className={cn('tabular-nums', n > 0 ? 'font-semibold text-text' : '')}>{n}</span>
        </li>
      ))}
    </ul>
  );
}

// ── Spend over time ──

const compact = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 });
const whole = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export interface SpendData {
  anyApproved: boolean;
  approved: string;
  committed: string;
  actual: string;
  variance: string;
  months: Array<{ month: string; committed: number; actual: number; cumCommitted: number; cumActual: number }>;
}

export function SpendChart({ programId, b }: { programId: string; b: SpendData }) {
  const approved = Number(b.approved);
  const figures = (
    <div className="mb-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
      {(
        [
          ['Approved', b.approved],
          ['Committed', b.committed],
          ['Actual', b.actual],
          ['Headroom', b.variance],
        ] as const
      ).map(([label, v]) => (
        <div key={label}>
          <p className="text-xs text-muted">{label}</p>
          <p className={cn('font-semibold tabular-nums', label === 'Headroom' && Number(v) < 0 && 'text-bad')}>
            {whole(Number(v))}
          </p>
        </div>
      ))}
    </div>
  );
  if (!b.anyApproved && !b.months.length)
    return (
      <p className="text-sm text-muted">
        No approved implementation budget yet. It is set once the Phase 2 estimate is accepted.{' '}
        <Link to={`/p/${programId}/budget`} className="text-brand-text hover:underline">
          Budget
        </Link>
      </p>
    );
  const months = b.months;
  const W = 640;
  const H = 250;
  const L = 56;
  const B = 24;
  const top = Math.max(approved, ...months.map((m) => m.cumCommitted), 1) * 1.08;
  const n = Math.max(1, months.length - 1);
  const x = (i: number) => L + (months.length === 1 ? (W - L - 10) / 2 : (i / n) * (W - L - 10));
  const y = (v: number) => 8 + (1 - v / top) * (H - B - 8);
  const line = (k: 'cumCommitted' | 'cumActual') =>
    months.map((m, i) => `${i ? 'L' : 'M'} ${x(i)} ${y(m[k])}`).join(' ');
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * top);
  const label = (m: string) =>
    `${MONTHS[Number(m.slice(5, 7)) - 1]}${m.endsWith('-01') || m === months[0]?.month ? ` ${m.slice(0, 4)}` : ''}`;
  return (
    <div>
      {figures}
      {months.length ? (
        <>
          <svg
            viewBox={`0 0 ${W} ${H}`}
            className="w-full"
            role="img"
            aria-label="Spend against approved budget by month"
          >
            {ticks.map((v) => (
              <g key={v}>
                <line x1={L} x2={W - 6} y1={y(v)} y2={y(v)} stroke="var(--border)" />
                <text x={L - 6} y={y(v) + 4} fontSize={10} textAnchor="end" fill="var(--muted)">
                  {compact(v)}
                </text>
              </g>
            ))}
            {approved > 0 && (
              <g>
                <line x1={L} x2={W - 6} y1={y(approved)} y2={y(approved)} stroke="var(--muted)" strokeDasharray="5 4" />
                <text x={W - 8} y={y(approved) - 5} fontSize={10} textAnchor="end" fill="var(--muted)">
                  Approved {compact(approved)}
                </text>
              </g>
            )}
            <path d={line('cumCommitted')} fill="none" stroke="var(--brand)" strokeWidth={2.2} />
            <path d={line('cumActual')} fill="none" stroke="var(--ok)" strokeWidth={2.2} />
            {months.map((m, i) => (
              <g key={m.month}>
                <circle cx={x(i)} cy={y(m.cumCommitted)} r={3} fill="var(--brand)">
                  <title>{`${label(m.month)}: committed to date ${whole(m.cumCommitted)}`}</title>
                </circle>
                <circle cx={x(i)} cy={y(m.cumActual)} r={3} fill="var(--ok)">
                  <title>{`${label(m.month)}: actual to date ${whole(m.cumActual)}`}</title>
                </circle>
                {(months.length <= 12 || i % Math.ceil(months.length / 12) === 0) && (
                  <text x={x(i)} y={H - 6} fontSize={10} textAnchor="middle" fill="var(--muted)">
                    {label(m.month)}
                  </text>
                )}
              </g>
            ))}
          </svg>
          <p className="mt-1 flex flex-wrap gap-x-4 text-xs text-muted">
            <span>
              <span className="mr-1 inline-block h-0.5 w-4 bg-brand align-middle" /> Committed to date
            </span>
            <span>
              <span className="mr-1 inline-block h-0.5 w-4 bg-ok align-middle" /> Actual to date
            </span>
            <span>
              <span className="mr-1 inline-block w-4 border-t border-dashed border-muted align-middle" /> Approved
            </span>
          </p>
        </>
      ) : (
        <p className="text-sm text-muted">No spend recorded yet.</p>
      )}
    </div>
  );
}

// ── Success criteria ──

const CRITERIA: Array<[string, string, string]> = [
  ['ACHIEVED', 'Achieved', 'bg-ok'],
  ['ON_TRACK', 'On track', 'bg-brand'],
  ['AT_RISK', 'At risk', 'bg-warn'],
  ['NOT_YET_MEASURABLE', 'Not yet measurable', 'bg-border-strong'],
];

export function CriteriaBar({ programId, statuses }: { programId: string; statuses: string[] | undefined }) {
  if (!statuses) return <p className="text-sm text-muted">Loading…</p>;
  if (!statuses.length) return <p className="text-sm text-muted">No success criteria recorded.</p>;
  const counts = CRITERIA.map(([k, label, cls]) => ({ k, label, cls, n: statuses.filter((s) => s === k).length }));
  return (
    <div>
      <div
        className="flex h-3 overflow-hidden rounded-full bg-surface-2"
        role="img"
        aria-label="Success criteria by status"
      >
        {counts
          .filter((c) => c.n)
          .map((c) => (
            <div
              key={c.k}
              className={c.cls}
              style={{ width: `${(c.n / statuses.length) * 100}%` }}
              title={`${c.label}: ${c.n}`}
            />
          ))}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-[13px]">
        {counts.map((c) => (
          <span key={c.k} className="flex items-center gap-1.5 text-muted">
            <span className={cn('size-2 rounded-full', c.cls)} />
            {c.label} <span className={cn('tabular-nums', c.n ? 'font-semibold text-text' : '')}>{c.n}</span>
          </span>
        ))}
        <Link
          to={`/p/${programId}/criteria`}
          className="ml-auto inline-flex items-center gap-1 text-xs text-brand-text hover:underline"
        >
          All {statuses.length} criteria <ArrowRight className="size-3" />
        </Link>
      </div>
    </div>
  );
}
